import { useCaseTypeNames } from '@/hooks/useCaseTypeNames';
import {
  resolveLocalizedCaseTypeLabel,
  resolveCaseTypeLabel,
} from '../../../../backend/lambdas/shared/case-type-labels';
/**
 * 区分値の英語の表 — 案件 (事件種別・案件ステータス・進捗ステータス)。複数の画面で使う。
 *
 * 領域ごとの表 (`i18n/enums/{domain}.ts`) の書き方の見本 (phase 2 の各 slice が同じ形で足す):
 *
 * - 日本語は既存の helper (`caseTypeLabel` / `caseStatusLabel` / `progressStatusLabel`) が正本。
 *   ここでは英語の対応表だけを持ち、日本語の表や fallback 規則は複製しない。
 * - 英語の表に無い値 (BE が区分を増やした直後など) は日本語の helper の結果を出す
 *   (空欄や raw enum を出さない。CLAUDE.md §2.4 の fallback 規則と同じ向き)。
 * - 英語の表が日本語の表の全キーを持つことは `enums/case.test.ts` が保証する (領域ごとに test を置く)。
 */
import { useMemo } from 'react';
import { CASE_TYPE_LABELS, caseTypeLabel, normalizeCaseType } from '@/constants/caseTypes';
import {
  CASE_STATUS_LABELS,
  PROGRESS_STATUS_LABELS,
  caseStatusLabel,
  progressStatusLabel,
} from '@/utils/caseStatusLabels';
import { useLocale } from '../LocaleProvider';
import type { Locale } from '../types';

/** 事件種別 (canonical key) の英語表記。 */
export const CASE_TYPE_LABELS_EN: Readonly<Record<string, string>> = {
  bankruptcy_dohai: 'Bankruptcy (no trustee)',
  bankruptcy_kanzai: 'Bankruptcy (with trustee)',
  individual_rehabilitation: 'Individual rehabilitation',
  civil_rehab_no_housing: 'Individual rehabilitation (without mortgage special clause)',
  civil_rehab_housing: 'Individual rehabilitation (with mortgage special clause)',
  overpayment: 'Overpayment refund',
  voluntary_arrangement: 'Voluntary debt settlement',
  labor: 'Labor',
  divorce: 'Divorce',
  traffic: 'Traffic accident',
  inheritance: 'Inheritance',
  corporate: 'Corporate',
  other: 'Other',
};

/** 事件種別が分からないときの英語表記 (日本語は `CASE_TYPE_UNKNOWN_FALLBACK`)。 */
export const CASE_TYPE_UNKNOWN_FALLBACK_EN = 'Case type not set';

/** 案件ステータス (canonical key) の英語表記。 */
export const CASE_STATUS_LABELS_EN: Readonly<Record<string, string>> = {
  active: 'In progress',
  completed: 'Completed',
  resigned: 'Withdrawn',
  deleted: 'Deleted',
};

/** 進捗ステータス (canonical key) の英語表記。 */
export const PROGRESS_STATUS_LABELS_EN: Readonly<Record<string, string>> = {
  collecting: 'Collecting',
  reviewing: 'Reviewing',
  completed: 'Completed',
  resigned: 'Withdrawn',
};

/** ステータスが分からないときの英語表記 (日本語は `CASE_STATUS_UNKNOWN_FALLBACK` = 未設定)。 */
export const STATUS_UNKNOWN_FALLBACK_EN = 'Not set';

/** 日本語ラベル → canonical key の逆引き (DB に日本語が直接入っている旧データ用)。 */
function keyByJapaneseLabel(
  labels: Readonly<Record<string, string>>,
  value: string,
): string | null {
  const hit = Object.entries(labels).find(([, label]) => label === value);
  return hit ? hit[0] : null;
}

/** 事件種別の表示 (表示言語別)。未知値は日本語 helper と同じく「未設定」系の表記。 */
export function localizedCaseTypeLabel(locale: Locale, value: string | null | undefined): string {
  if (locale === 'ja') return caseTypeLabel(value);
  if (!value) return CASE_TYPE_UNKNOWN_FALLBACK_EN;
  const normalized = normalizeCaseType(value);
  const key = CASE_TYPE_LABELS[normalized]
    ? normalized
    : keyByJapaneseLabel(CASE_TYPE_LABELS, value);
  if (!key) return CASE_TYPE_UNKNOWN_FALLBACK_EN;
  return CASE_TYPE_LABELS_EN[key] ?? caseTypeLabel(value);
}

/** 見出し用の事件種別 (日本語は「〜事件」、英語は種別名のみ)。 */
export function localizedCaseTypeTitle(locale: Locale, value: string | null | undefined): string {
  return locale === 'ja' ? `${caseTypeLabel(value)}事件` : localizedCaseTypeLabel(locale, value);
}

/**
 * 既定の案件名の末尾 (案件の作成時に「事件種別の日本語ラベル + 事件」で付く。
 * `pages/CasesPage.tsx` の作成処理。保存する案件名は表示言語に依らず日本語)。
 */
const DEFAULT_CASE_TITLE_SUFFIX_JA = '事件';

/**
 * 案件名の表示 (表示言語別、2026-09-25 英語表示の崩れの修正)。
 *
 * 案件名はデータなので原則は訳さない (ADR-081 決定 2)。ただし案件の作成時に自動で付く
 * **既定の名前** (「個人再生事件」「自己破産(同廃)事件」= 事件種別の日本語ラベル + 事件) は
 * 事務所が付けた名前ではなく区分値から作った文なので、英語表示では「事件種別の英語 + case」にする
 * (例: 「Individual rehabilitation case」)。
 *
 * - 訳すのは、案件名が「`CASE_TYPE_LABELS` のいずれかのラベル + 事件」と**完全に一致**するときだけ。
 *   事務所が付けた名前・依頼者名入り・部分一致 (「個人再生事件 (再申立)」など)・未知の事件種別は、
 *   データとしてそのまま返す。
 * - 日本語表示は常にそのまま返す。空・null は空文字。
 * - 比較・検索・保存には使わない (表示の直前だけ)。
 */
export function localizedCaseTitle(locale: Locale, title: string | null | undefined): string {
  const value = title ?? '';
  if (locale === 'ja' || !value.endsWith(DEFAULT_CASE_TITLE_SUFFIX_JA)) return value;
  const label = value.slice(0, -DEFAULT_CASE_TITLE_SUFFIX_JA.length);
  const key = label ? keyByJapaneseLabel(CASE_TYPE_LABELS, label) : null;
  const english = key ? CASE_TYPE_LABELS_EN[key] : undefined;
  return english ? `${english} case` : value;
}

/** 案件ステータスの表示 (表示言語別)。 */
export function localizedCaseStatusLabel(locale: Locale, value: string | null | undefined): string {
  if (locale === 'ja') return caseStatusLabel(value);
  if (!value) return STATUS_UNKNOWN_FALLBACK_EN;
  const key = Object.prototype.hasOwnProperty.call(CASE_STATUS_LABELS, value)
    ? value
    : keyByJapaneseLabel(CASE_STATUS_LABELS, value);
  if (!key) return STATUS_UNKNOWN_FALLBACK_EN;
  return CASE_STATUS_LABELS_EN[key] ?? caseStatusLabel(value);
}

/** 進捗ステータスの表示 (表示言語別)。 */
export function localizedProgressStatusLabel(
  locale: Locale,
  value: string | null | undefined,
): string {
  if (locale === 'ja') return progressStatusLabel(value);
  if (!value) return STATUS_UNKNOWN_FALLBACK_EN;
  const key = Object.prototype.hasOwnProperty.call(PROGRESS_STATUS_LABELS, value)
    ? value
    : keyByJapaneseLabel(PROGRESS_STATUS_LABELS, value);
  if (!key) return STATUS_UNKNOWN_FALLBACK_EN;
  return PROGRESS_STATUS_LABELS_EN[key] ?? progressStatusLabel(value);
}

/** 現在の表示言語に束ねた区分ラベル関数 (画面の中で使う)。 */
export function useCaseLabels() {
  const { locale } = useLocale();
  const { names, ready, requireReady } = useCaseTypeNames();
  return useMemo(() => {
    const label = (value: string | null | undefined, fallback?: string) =>
      ready
        ? resolveLocalizedCaseTypeLabel(value, names, locale, CASE_TYPE_LABELS_EN, fallback)
        : '…';
    return {
      caseType: label,
      caseTypeJa: (value: string | null | undefined, fallback?: string) =>
        ready ? resolveCaseTypeLabel(value, names, fallback) : '…',
      ready,
      requireReady,
      caseTypeTitle: (value: string | null | undefined) =>
        locale === 'ja' ? `${label(value)}事件` : label(value),
      caseTitle: (title: string | null | undefined) => localizedCaseTitle(locale, title),
      caseStatus: (value: string | null | undefined) => localizedCaseStatusLabel(locale, value),
      progressStatus: (value: string | null | undefined) =>
        localizedProgressStatusLabel(locale, value),
    };
  }, [locale, names, ready, requireReady]);
}
